import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from '../../api/api';
import { METHODS, SERVICES } from '../../api/constants';
import { commonMoodlePayload } from '../../config';
import {
  MOODLE_BACKEND_ROUTES,
  VASSIST_BACKEND_ROUTES
} from '../../config/apiRoutes';
import SuccessToastMessage from '../../@common/SuccessToast/SuccessToast';
import { toast } from 'react-toastify';
import { parseLSQData } from '../../constant/profileWidgetConstant';
import { ACCESS_TYPE, handleEnabledState } from '../../constant/constant';
import { createCleverTapProfile } from '../../utils/cleverTap';

/**
 * Extracts the max numeric value from a range string (e.g., "6-7" → "7", "7-8" → "8").
 * If the value is not a range, returns it as-is after converting to string.
 */
const extractMaxFromRange = (value) => {
  if (value === null || value === undefined) return '0';
  // Remove non-numeric text like "years", "months", "lakhs", etc., keeping digits, dots, hyphens
  const str = value.toString().trim().replace(/[^0-9.\-]/g, '');
  if (str.includes('-')) {
    const parts = str.split('-').map(p => parseFloat(p.trim())).filter(n => !isNaN(n));
    if (parts.length > 0) return Math.max(...parts).toString();
  }
  const num = parseFloat(str);
  return !isNaN(num) ? num.toString() : '0';
};

/**
 * Normalizes workExperience.totalWorkExperience to match backend schema:
 * { years: String, months: String }
 *
 * Backend schema expects: totalWorkExperience: { years: String, months: String }
 * This function ensures data matches the schema and handles backward compatibility
 * for legacy data formats that might still exist.
 *
 * @param {Object} profileData - The profile data object to normalize
 * @returns {Object} - The profile data with normalized workExperience
 */
const normalizeWorkExperience = (profileData) => {
  if (!profileData) return profileData;
  
  // Initialize workExperience if it doesn't exist
  if (!profileData.workExperience) {
    profileData.workExperience = {};
  }
  
  const workExp = profileData.workExperience;
  
  // Case 1: totalWorkExperience exists and is already an object (expected format)
  if (workExp.totalWorkExperience && typeof workExp.totalWorkExperience === 'object') {
    // Ensure years and months are strings, extract max if range (e.g., "6-7" → "7")
    workExp.totalWorkExperience = {
      years: extractMaxFromRange(workExp.totalWorkExperience.years),
      months: extractMaxFromRange(workExp.totalWorkExperience.months)
    };
  }
  // Case 2: totalWorkExperience exists but is NOT an object (legacy format: string/number)
  else if (workExp.totalWorkExperience && typeof workExp.totalWorkExperience !== 'object') {
    // Convert old format to new format matching backend schema
    workExp.totalWorkExperience = {
      years: extractMaxFromRange(workExp.totalWorkExperience),
      months: extractMaxFromRange(workExp.totalWorkExperienceinMonths || workExp.workExperienceMonths)
    };
  }
  // Case 3: totalWorkExperience doesn't exist but workExperienceYears/workExperienceMonths do (intermediate format)
  else if (!workExp.totalWorkExperience && (workExp.workExperienceYears !== undefined || workExp.workExperienceMonths !== undefined)) {
    // Convert intermediate format to backend schema format
    workExp.totalWorkExperience = {
      years: extractMaxFromRange(workExp.workExperienceYears),
      months: extractMaxFromRange(workExp.workExperienceMonths)
    };
  }
  // Case 4: totalWorkExperience doesn't exist, check if years/months are at root level
  else if (!workExp.totalWorkExperience) {
    if (workExp.years !== undefined || workExp.months !== undefined) {
      workExp.totalWorkExperience = {
        years: workExp.years?.toString() || '0',
        months: workExp.months?.toString() || '0'
      };
    } else {
      // Case 5: No data exists - initialize with defaults matching backend schema
      workExp.totalWorkExperience = {
        years: '0',
        months: '0'
      };
    }
  }
  
  // Normalize salaryPerAnnum: extract max if range (e.g., "6-7" → "7")
  if (workExp.salaryPerAnnum) {
    workExp.salaryPerAnnum = extractMaxFromRange(workExp.salaryPerAnnum);
  }

  return profileData;
};

export const fetchPolicyDetails = createAsyncThunk(
  'token/fetchPolicyDetails',
  async (param) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.POST,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_POLICY_DETAILS
        }
      );
      return { data: response.data };
    } catch (e) {}
  }
);

export const postProfileDetails = createAsyncThunk(
  // no longer in use
  'token/postProfileDetails',
  async (data, thunkApi) => {
    const response = await makeRequest(
      SERVICES.VASSIST_SERVICE,
      VASSIST_BACKEND_ROUTES.POST_PROFILE_DATA,
      METHODS.POST,
      {},
      data
    );
    if (response.status === 200) {
      thunkApi.dispatch(setLSQ({ isLsqData: false, data: {}, dataSync: true }));
      return data;
    }
    return response.data;
  }
);

export const GET_LSQ_PROFILE_DETAILS = (data) => {
  // no longer in use
  return new Promise(async (resolve, reject) => {
    try {
      const response = await makeRequest(
        SERVICES.VASSIST_SERVICE,
        VASSIST_BACKEND_ROUTES.GET_LSQ_PROFILE_DETAILS,
        METHODS.GET,
        data
      );
      if (response.status === 200) {
        if (response?.data?.length) {
          const parsedData = await parseLSQData(response?.data[0]);
          // Normalize workExperience data to ensure consistency
          const normalizedData = normalizeWorkExperience(parsedData);
          if (normalizedData?.phone?.length) {
            resolve({ ...response, data: normalizedData });
          } else {
            reject({});
          }
        } else {
          reject(response);
        }
      } else {
        reject(response);
      }
    } catch (e) {
      reject(e);
    }
  });
};

// Need to use promise and to make it work for every pre-written code avoiding createasyncthunk and using normal promises
// Due to which need to pass state and dispatch.
// If you move to redux just return where is resolve and thunkApi.rejectWithvalue on reject else all logic will be same.
export const GET_PROFILE_DETAILS = (data, dispatch, lsqDataParam) => {
  return new Promise(async (resolve, reject) => {
    dispatch(setProfileLoading(true));
    if (lsqDataParam?.isLsqData) {
      resolve({ status: 200, data: lsqDataParam?.lsqData });
      dispatch(setProfileLoading(false));
      return;
    }
    try {
      const profileData = await makeRequest(
        SERVICES.VLEARN_PROFILE_SERVICE,
        VASSIST_BACKEND_ROUTES.GET_PROFILE_DATA_NEW,
        METHODS.POST,
        {},
        data,
        [{ key: 'x-hv-secret-key', value: 'bm8dmzbvtbCOZnrFYZoKp7RyoQavlid4' }]
      );
      if (profileData.status === 200) {
        const transformedData = normalizeWorkExperience(profileData?.data?.data);
        
        dispatch(setProfileData(transformedData));
        dispatch(
          setTabEnabledState(handleEnabledState(transformedData))
        );
        resolve({ ...profileData, data: transformedData });
        const {
          firstName,
          lastName,
          gender,
          phone,
          emailAddress,
          dateOfBirth
        } = profileData?.data?.data;
        const profileCreated = sessionStorage.getItem('profileCreated');
        if (!profileCreated) {
          createCleverTapProfile({
            Name: firstName + lastName,
            Phone: '+91' + phone,
            'First Name': firstName,
            'Last Name': lastName,
            Gender: gender[0],
            DOB: dateOfBirth ? new Date(dateOfBirth.split(',')[0]) : undefined,
            'Phone Number': '+91' + phone,
            Email: emailAddress
          });
        }
        dispatch(setProfileLoading(false));
        return;
      }
      // else if (profileData.status === 204 && !lsqDataParam?.isLsqData) {
      //   if (lsqDataParam?.accessType === ACCESS_TYPE.Premium) {
      //     console.log('asFDSGADFS');

      //     // LSQ api call
      //     dispatch(lsqLoading(true));
      //     await GET_LSQ_PROFILE_DETAILS(data)
      //       .then((res) => {
      //         dispatch(
      //           setLSQ({ isLsqData: true, data: res?.data, loading: false })
      //         );
      //         resolve(res);
      //         dispatch(postProfileDetails(res?.data));
      //       })
      //       .catch((e) => {
      //         dispatch(setLSQ({ isLsqData: false, data: {}, loading: false }));
      //         reject(e);
      //       });
      //   }
      //   resolve(profileData);
      //   dispatch(setProfileLoading(false));
      //   return;
      // }
    } catch (e) {
      const { response } = e;

      // if (response?.status === 404 && !lsqDataParam?.isLsqData) {
      //   console.log('asFDSGADFS');

      //   dispatch(lsqLoading(true));
      //   await GET_LSQ_PROFILE_DETAILS(data)
      //     .then((res) => {
      //       dispatch(
      //         setLSQ({ isLsqData: true, data: res?.data, loading: false })
      //       );
      //       resolve(res);
      //       dispatch(postProfileDetails(res?.data));
      //     })
      //     .catch((e) => {
      //       dispatch(setLSQ({ isLsqData: false, data: {}, loading: false }));
      //       reject(e);
      //     });
      //   dispatch(setProfileLoading(false));
      //   return;
      // }
      reject(e);
      dispatch(setProfileLoading(false));
    }
  });
};
export const postPolicyDetails = createAsyncThunk(
  'token/postPolicyDetails',
  async (param) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.POST,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.POST_POLICY_DETAILS
        }
      );
      return { data: response.data };
    } catch (e) {}
  }
);

export const getProfileDetails = createAsyncThunk(
  'profileDetails/getProfileDetails',
  async (param, thunkApi) => {
    try {
      const lpsData = thunkApi.getState()?.program?.programData;
      let lpidNameMapping = {};
      lpsData?.forEach((obj) => (lpidNameMapping[obj?.lpid] = obj?.lpname));

      const profileData = await makeRequest(
        SERVICES.VLEARN_PROFILE_SERVICE,
        VASSIST_BACKEND_ROUTES.GET_PROFILE_DATA_NEW,
        METHODS.POST,
        {},
        {
          filter: {
            phone: param.phone,
            userId: thunkApi.getState()?.auth?.userData?.userid
          },
          payload: {
            enrolledLearningPaths: lpidNameMapping,
            userId: thunkApi.getState()?.auth?.userData?.userid,
            phone: param.phone,
            emailAddress: thunkApi.getState()?.auth?.userData?.email
          }
        },
        [{ key: 'x-hv-secret-key', value: 'bm8dmzbvtbCOZnrFYZoKp7RyoQavlid4' }]
      );
      if (profileData?.status === 200) {
        // Normalize workExperience data to ensure totalWorkExperience is in { years, months } format
        // normalizeWorkExperience mutates the object in place
        normalizeWorkExperience(profileData?.data?.data);
        
        thunkApi.dispatch(
          setTabEnabledState(handleEnabledState(profileData?.data?.data))
        );
      }
      return { data: profileData.data };
    } catch (e) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const submitProfileDetails = createAsyncThunk(
  'submitProfileDetails/submitProfileDetails',
  async (data1, thunkApi) => {
    const lpsData = thunkApi.getState()?.program?.programData;
    let lpidNameMapping = {};
    lpsData?.forEach((obj) => (lpidNameMapping[obj?.lpid] = obj?.lpname));
    let data = { ...(data1?.cb ? data1?.data : data1) };
    let temp = data?.dateOfBirth?.split('-');
    data.dateOfBirth =
      temp[temp?.length - 1]?.length >= 4
        ? temp?.reverse()?.join('-')
        : data?.dateOfBirth;

    const formattedPhone = data?.phone.replace('+91-', '');
    const response = await makeRequest(
      SERVICES.VLEARN_PROFILE_SERVICE,
      VASSIST_BACKEND_ROUTES.POST_PROFILE_DATA_NEW,
      METHODS.POST,
      {},
      {
        ...data,
        profileCompletion: thunkApi.getState().profile.profileCompletionRedux,
        userId: thunkApi.getState()?.auth?.userData?.userid,
        enrolledLearningPaths: lpidNameMapping
      },
      [{ key: 'x-hv-secret-key', value: 'bm8dmzbvtbCOZnrFYZoKp7RyoQavlid4' }]
    );
    if (
      response.status === 200 &&
      !(response?.data?.errorcode || response?.data?.exception)
    ) {
      thunkApi.dispatch(getProfileDetails({ phone: formattedPhone }));
      if (data1?.cb) {
        data1?.cb();
      }
      return data;
    }
    return response.data;
  }
);

export const authWithToken = createAsyncThunk(
  'token/authWithToken',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.PAYMENTS_MIDDLEWARE_SERVICE,
        '/webhook/fetch-token',
        METHODS.POST,
        {},
        {
          token: param?.token
        }
      );

      if (response?.data?.response?.token && response?.data?.response?.userid) {
        param.successCb(response?.data?.response);
      } else {
        param.failureCb();
      }
      
      return response?.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const userSlice = createSlice({
  name: 'token',
  initialState: {
    userToken: '',
    authWithTokenData: {},
    isAuthWithTokenLoading: false,
    isAuthWithTokenError: false,
    policyDetails: [],
    policyLoading: false,
    policyAcceptedSuccess: false,
    isLsqData: false,
    lsqData: {},
    isLsqLoading: false,
    isDataSync: false,
    profileLoading: false,
    profileData: {},
    tabEnabledState: {
      isPersonalDetailsEnabled: true,
      isProfessionalDetailsEnabled: false,
      isEducationalQualificationsEnabled: false,
      isSkillProficiencyEnabled: false,
      isCareersInformationEnabled: false
    },
    profileDetails: {
      isLoading: false,
      profileData: {},
      error: ''
    }
  },
  reducers: {
    userToken: (state, action) => {
      state.userToken = action.payload;
    },
    setProfileData: (state, action) => {
      state.profileData = action.payload;
    },
    setTabEnabledState: (state, action) => {
      state.tabEnabledState = action.payload;
    },
    setLSQ: (state, action) => {
      state.isLsqData = action.payload.isLsqData;
      state.lsqData = action.payload.data;
      state.isLsqLoading = action.payload.loading || state.isLsqLoading;
      if (action.payload?.dataSync) {
        state.isDataSync = action.payload.dataSync;
      }
    },
    lsqLoading: (state, action) => {
      state.isLsqLoading = action.payload;
    },
    setProfileLoading: (state, action) => {
      state.isLsqLoading = action.payload;
    },
    setPolicyAccepted: (state) => {
      state.policyAcceptedSuccess = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPolicyDetails.pending, (state) => {
      state.policyLoading = true;
    });
    builder.addCase(fetchPolicyDetails.fulfilled, (state, action) => {
      state.policyLoading = false;
      if (action?.payload?.data?.length) {
        state.policyDetails = action?.payload?.data;
      }
    });
    builder.addCase(fetchPolicyDetails.rejected, (state) => {
      state.policyLoading = false;
    });
    builder.addCase(postPolicyDetails.pending, (state) => {
      state.policyLoading = true;
    });
    builder.addCase(postPolicyDetails.fulfilled, (state, action) => {
      state.policyLoading = false;
      if (action.payload.data.status === 'Success') {
        state.policyAcceptedSuccess = true;
      } else {
        toast.error(action.payload.data.status || 'Something went wrong');
      }
    });
    builder.addCase(postPolicyDetails.rejected, (state) => {
      state.policyLoading = false;
    });
    builder.addCase(getProfileDetails.pending, (state) => {
      state.profileDetails.isLoading = true;
      state.profileData.leanerApiData = {};
    });
    builder.addCase(getProfileDetails.fulfilled, (state, action) => {
      state.profileDetails.isLoading = false;
      state.profileData.leanerApiData = action?.payload?.data?.data || {};
    });
    builder.addCase(getProfileDetails.rejected, (state) => {
      state.profileDetails.isLoading = false;
      state.profileData.leanerApiData = {};
    });
    builder.addCase(authWithToken.pending, (state) => {
      state.isAuthWithTokenLoading = true;
      state.authWithTokenData = {};
      state.isAuthWithTokenError = false;
    });
    builder.addCase(authWithToken.fulfilled, (state, action) => {
      state.isAuthWithTokenLoading = false;
      state.isAuthWithTokenError = false;
      state.authWithTokenData = action?.payload?.response || {};
    });
    builder.addCase(authWithToken.rejected, (state) => {
      state.isAuthWithTokenLoading = false;
      state.authWithTokenData = {};
      state.isAuthWithTokenError = true;
    });
  }
});

// Action creators are generated for each case reducer function
export const {
  userToken,
  setLSQ,
  lsqLoading,
  setProfileLoading,
  setProfileData,
  setTabEnabledState,
  setPolicyAccepted
} = userSlice.actions;

export default userSlice.reducer;
